/** A geometria do desenho da caixa e dos planos de corte no ecrã.
 *
 *  O desenho antigo escondia a caixa inteira mal um dos seus cantos
 *  ficasse atrás da câmara. Como a caixa nasce do tamanho do modelo,
 *  bastava aproximar a câmara ou entrar no edifício para ela e as pegas
 *  desaparecerem, e a pessoa ficava sem forma de a ver nem de a mexer.
 *  Aqui recorta-se cada aresta e cada face contra o plano próximo da
 *  câmara (Sutherland-Hodgman), e desenha-se o bocado que fica à frente.
 *
 *  Puro, sem three.js: recebe matrizes como listas de 16 números (a
 *  ordem por colunas do three.js) e devolve pontos no ecrã. */

export type V3 = readonly [number, number, number]
export interface P2 {
  x: number
  y: number
}

/** Aplica uma matriz 4x4 (por colunas) a um ponto. */
export function aplicar(m: readonly number[], p: V3): [number, number, number, number] {
  return [
    m[0] * p[0] + m[4] * p[1] + m[8] * p[2] + m[12],
    m[1] * p[0] + m[5] * p[1] + m[9] * p[2] + m[13],
    m[2] * p[0] + m[6] * p[1] + m[10] * p[2] + m[14],
    m[3] * p[0] + m[7] * p[1] + m[11] * p[2] + m[15],
  ]
}

/** Um pouco à frente do olho: o plano contra o qual se recorta. Em
 *  espaço de câmara, "à frente" é z negativo. */
export const Z_PROXIMO = -0.05

function interp(a: V3, b: V3, t: number): V3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
}

/** Recorta um segmento (em espaço de câmara) ao que fica à frente. */
export function recortarSegmento(a: V3, b: V3, z = Z_PROXIMO): [V3, V3] | null {
  const aDentro = a[2] <= z
  const bDentro = b[2] <= z
  if (aDentro && bDentro) return [a, b]
  if (!aDentro && !bDentro) return null
  const t = (z - a[2]) / (b[2] - a[2])
  const corte = interp(a, b, t)
  return aDentro ? [a, corte] : [corte, b]
}

/** Recorta um polígono convexo (em espaço de câmara) ao que fica à
 *  frente. Pode sair vazio. */
export function recortarPoligono(pontos: readonly V3[], z = Z_PROXIMO): V3[] {
  const fora: V3[] = []
  for (let i = 0; i < pontos.length; i++) {
    const a = pontos[i]
    const b = pontos[(i + 1) % pontos.length]
    const aDentro = a[2] <= z
    const bDentro = b[2] <= z
    if (aDentro) fora.push(a)
    if (aDentro !== bDentro) fora.push(interp(a, b, (z - a[2]) / (b[2] - a[2])))
  }
  return fora
}

/** Espaço de câmara -> píxeis. */
export function projectar(pCam: V3, projeccao: readonly number[], largura: number, altura: number): P2 | null {
  const c = aplicar(projeccao, pCam)
  if (Math.abs(c[3]) < 1e-12) return null
  const x = c[0] / c[3]
  const y = c[1] / c[3]
  return { x: ((x + 1) / 2) * largura, y: ((1 - y) / 2) * altura }
}

export interface Camara {
  /** matrixWorldInverse, por colunas. */
  vista: readonly number[]
  /** projectionMatrix, por colunas. */
  projeccao: readonly number[]
  largura: number
  altura: number
}

function paraCamara(c: Camara, p: V3): V3 {
  const r = aplicar(c.vista, p)
  return [r[0], r[1], r[2]]
}

/** Uma aresta no mundo, desenhada: o bocado à frente da câmara. */
export function arestaNoEcra(c: Camara, a: V3, b: V3): [P2, P2] | null {
  const s = recortarSegmento(paraCamara(c, a), paraCamara(c, b))
  if (!s) return null
  const pa = projectar(s[0], c.projeccao, c.largura, c.altura)
  const pb = projectar(s[1], c.projeccao, c.largura, c.altura)
  return pa && pb ? [pa, pb] : null
}

/** Uma face (polígono convexo no mundo), desenhada. */
export function poligonoNoEcra(c: Camara, pontos: readonly V3[]): P2[] {
  const recortado = recortarPoligono(pontos.map((p) => paraCamara(c, p)))
  const fora: P2[] = []
  for (const p of recortado) {
    const q = projectar(p, c.projeccao, c.largura, c.altura)
    if (q) fora.push(q)
  }
  return fora.length >= 3 ? fora : []
}

/** Um ponto no mundo, desenhado; null se estiver atrás da câmara. */
export function pontoNoEcra(c: Camara, p: V3): P2 | null {
  const q = paraCamara(c, p)
  if (q[2] > Z_PROXIMO) return null
  return projectar(q, c.projeccao, c.largura, c.altura)
}

// --- Planos soltos: onde se desenham e como se inclinam ---------------

function dot(a: V3, b: V3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}
function cross(a: V3, b: V3): V3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
}
function norm(a: V3): V3 {
  const l = Math.hypot(a[0], a[1], a[2]) || 1
  return [a[0] / l, a[1] / l, a[2] / l]
}

/** Duas direcções sobre o plano, perpendiculares entre si e à normal.
 *  Para um plano horizontal dá x e z; para um vertical, a horizontal e
 *  a vertical dele. */
export function baseDoPlano(normal: V3): [V3, V3] {
  const n = norm(normal)
  const ref: V3 = Math.abs(n[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0]
  const u = norm(cross(ref, n))
  const w = norm(cross(n, u))
  return [u, w]
}

export interface Caixa {
  min: V3
  max: V3
}

/**
 * O contorno do plano dentro da caixa do modelo: o polígono que ele
 * corta no volume. É o que se desenha, para o plano se ver do tamanho
 * do edifício e não como um quadrado infinito. Vazio se o plano passar
 * ao lado do modelo.
 */
export function poligonoDoPlano(normal: V3, ponto: V3, caixa: Caixa): V3[] {
  const [u, w] = baseDoPlano(normal)
  const d = Math.hypot(caixa.max[0] - caixa.min[0], caixa.max[1] - caixa.min[1], caixa.max[2] - caixa.min[2]) || 1
  // O centro da caixa, levado ao plano: um quadrado grande à volta dele
  // cobre sempre o corte.
  const centro: V3 = [
    (caixa.min[0] + caixa.max[0]) / 2,
    (caixa.min[1] + caixa.max[1]) / 2,
    (caixa.min[2] + caixa.max[2]) / 2,
  ]
  const n = norm(normal)
  const dist = dot(n, [centro[0] - ponto[0], centro[1] - ponto[1], centro[2] - ponto[2]])
  const c0: V3 = [centro[0] - n[0] * dist, centro[1] - n[1] * dist, centro[2] - n[2] * dist]
  let poli: V3[] = [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ].map(([a, b]) => [c0[0] + (u[0] * a + w[0] * b) * d, c0[1] + (u[1] * a + w[1] * b) * d, c0[2] + (u[2] * a + w[2] * b) * d] as V3)
  // Recortar contra as seis faces da caixa.
  for (let eixo = 0; eixo < 3; eixo++) {
    for (const lado of [0, 1] as const) {
      const limite = lado === 0 ? caixa.min[eixo] : caixa.max[eixo]
      const dentro = (p: V3) => (lado === 0 ? p[eixo] >= limite - 1e-9 : p[eixo] <= limite + 1e-9)
      const novo: V3[] = []
      for (let i = 0; i < poli.length; i++) {
        const a = poli[i]
        const b = poli[(i + 1) % poli.length]
        const ad = dentro(a)
        const bd = dentro(b)
        if (ad) novo.push(a)
        if (ad !== bd) novo.push(interp(a, b, (limite - a[eixo]) / (b[eixo] - a[eixo])))
      }
      poli = novo
      if (poli.length === 0) return []
    }
  }
  return poli
}

export function centroide(pontos: readonly V3[]): V3 | null {
  if (pontos.length === 0) return null
  let x = 0, y = 0, z = 0
  for (const p of pontos) {
    x += p[0]; y += p[1]; z += p[2]
  }
  return [x / pontos.length, y / pontos.length, z / pontos.length]
}

/** Roda um vector em torno de um eixo unitário (Rodrigues). */
export function rodar(v: V3, eixo: V3, angulo: number): V3 {
  const k = norm(eixo)
  const c = Math.cos(angulo)
  const s = Math.sin(angulo)
  const kv = cross(k, v)
  const kd = dot(k, v) * (1 - c)
  return [
    v[0] * c + kv[0] * s + k[0] * kd,
    v[1] * c + kv[1] * s + k[1] * kd,
    v[2] * c + kv[2] * s + k[2] * kd,
  ]
}

/**
 * Quanto o rato andou ao longo de uma direcção do mundo, em metros.
 *
 * Projecta-se a direcção no ecrã a partir de um ponto de apoio, e mede-
 * -se a componente do movimento do rato nessa direcção. É a mesma conta
 * das setas da caixa: arrastar uma seta é andar na direcção dela.
 */
export function deslocamentoAoLongo(
  c: Camara,
  apoio: V3,
  direccao: V3,
  rato: P2,
  ratoInicial: P2,
): number | null {
  const p0 = pontoNoEcra(c, apoio)
  const p1 = pontoNoEcra(c, [apoio[0] + direccao[0], apoio[1] + direccao[1], apoio[2] + direccao[2]])
  if (!p0 || !p1) return null
  const ex = p1.x - p0.x
  const ey = p1.y - p0.y
  const l2 = ex * ex + ey * ey
  if (l2 < 0.01) return null
  return ((rato.x - ratoInicial.x) * ex + (rato.y - ratoInicial.y) * ey) / l2
}

// --- Rodar como um volante --------------------------------------------

function menos3(a: V3, b: V3): V3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
}

/** Onde o raio do rato bate no plano de rotação (o que passa pelo
 *  `centro` e é perpendicular ao `eixo`). Null quando o raio vai quase
 *  rasante a esse plano, ou para trás: aí a conta do ângulo não presta. */
export function raioNoPlanoDeRotacao(
  origem: V3,
  direccao: V3,
  centro: V3,
  eixo: V3,
  rasante = 0.2,
): V3 | null {
  const k = norm(eixo)
  const d = norm(direccao)
  const den = dot(d, k)
  if (Math.abs(den) < rasante) return null
  const t = dot(menos3(centro, origem), k) / den
  if (!Number.isFinite(t) || t < 0) return null
  return [origem[0] + d[0] * t, origem[1] + d[1] * t, origem[2] + d[2] * t]
}

/** O ângulo, com sinal (regra da mão direita à volta do `eixo`), que
 *  leva `de` a `para`, os dois vistos no plano perpendicular ao eixo.
 *  É o ângulo que o rato descreveu à volta do centro. */
export function anguloAVolta(de: V3, para: V3, eixo: V3): number {
  const k = norm(eixo)
  const a = menos3(de, [k[0] * dot(k, de), k[1] * dot(k, de), k[2] * dot(k, de)])
  const b = menos3(para, [k[0] * dot(k, para), k[1] * dot(k, para), k[2] * dot(k, para)])
  return Math.atan2(dot(k, cross(a, b)), dot(a, b))
}

/** Pontos de um círculo no mundo (para desenhar o volante). */
export function circulo(centro: V3, eixo: V3, raio: number, n = 64): V3[] {
  const [u, w] = baseDoPlano(eixo)
  const out: V3[] = []
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2
    const c = Math.cos(a) * raio
    const s = Math.sin(a) * raio
    out.push([centro[0] + u[0] * c + w[0] * s, centro[1] + u[1] * c + w[1] * s, centro[2] + u[2] * c + w[2] * s])
  }
  return out
}
